import type { components } from '@app/api/types';
import type { DecisionDetail } from './Decisions.api';
import type { Decision } from './vocabulario';

/** As claims do modo Claims (JSON) do Simulador (T18), na forma do contrato. */
export type ReplayClaims = NonNullable<components['schemas']['SimulateRequest']['claims']>;
type ClaimValue = ReplayClaims[string];

/** O nome da claim do perfil, o mesmo que o PDP lê (`claims.perfil` em `authz.rego`). */
const PROFILE_CLAIM = 'perfil';

/**
 * As claims com que «Reproduzir no simulador», no Detalhe da decisão (T17), abre o Simulador (T18) no modo Claims
 * (aim-78s, protótipo aprovado pelo mrolim em 11/10/2026). É o que a decisão guardou da pessoa, e nunca o token: o
 * produto não guarda o token da pessoa (regra 12 do CLAUDE.md). Por isso a simulação vale para alguém com estas
 * claims, e não para o token daquela chamada.
 *
 * - `sub`, `perfil`, `iss` e `aud`: as colunas da decisão, com os nomes das claims que o PDP lê. O detalhe vem
 *   primeiro, e a linha da lista cobre o `sub` e o perfil enquanto o detalhe não chegou.
 * - Os valores que as condições das regras leram: o `received` da condição que barrou cada regra avaliada. É o único
 *   valor de condição que a decisão guarda. A condição que casou não guarda o valor que leu, e um valor `null` quer
 *   dizer que o token não tinha a claim: ausente, e não escrito. As colunas vencem o `received` da mesma claim.
 *
 * Nulo quando a decisão não guardou nada da pessoa (a chamada sem token, o token que o PDP não aceitou). Ali o
 * Simulador abre como antes, no modo JWT e sem pessoa.
 */
export function replayClaims(
  detail: DecisionDetail | undefined,
  row: Decision | undefined,
): ReplayClaims | null {
  const claims: ReplayClaims = {};
  const user = detail?.user;
  const sub = user?.sub ?? row?.sub;
  const profile = user?.profile ?? row?.profile;
  if (sub) claims.sub = sub;
  if (profile) claims[PROFILE_CLAIM] = profile;
  if (user?.issuer) claims.iss = user.issuer;
  if (user?.audience && user.audience.length > 0) claims.aud = [...user.audience];

  for (const rule of detail?.diagnostic?.person_axis?.evaluated_rules ?? []) {
    const claim = rule.failed_condition?.claim?.trim();
    if (!claim || claim in claims) continue;
    const value = claimValue(rule.failed_condition?.received);
    if (value !== undefined) claims[claim] = value;
  }
  return Object.keys(claims).length > 0 ? claims : null;
}

/** Texto, número, booleano ou lista de textos: a forma que o contrato aceita. O resto fica fora. */
function claimValue(value: unknown): ClaimValue | undefined {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) return [...value];
  return undefined;
}
